import api from './client';

// Journal API
export const journalAPI = {
  // Entries
  createEntry: (data: {
    entryType: 'daily' | 'note' | 'weekly_review' | 'task_reflection' | 'mood_checkin';
    content: string;
    title?: string;
    entryDate?: string;
    mood?: 'great' | 'good' | 'okay' | 'low' | 'rough';
    energyLevel?: number;
    mediaIds?: number[];
    taskIds?: string[];
    tagIds?: number[];
    visibility?: 'private' | 'partners' | 'hierarchy';
  }) => api.post('/journal/entries', data),

  getEntry: (entryId: string) => api.get(`/journal/entries/${entryId}`),

  listEntries: (params?: {
    entryType?: 'daily' | 'note' | 'weekly_review' | 'task_reflection' | 'mood_checkin';
    fromDate?: string;
    toDate?: string;
    search?: string;
    tagIds?: number[];
    mood?: 'great' | 'good' | 'okay' | 'low' | 'rough';
    limit?: number;
    offset?: number;
    includeShared?: boolean;
  }) => {
    const queryParams = params ? {
      ...params,
      tagIds: params.tagIds?.join(','),
      include_shared: params.includeShared,
      includeShared: undefined,
    } : undefined;
    return api.get('/journal/entries', { params: queryParams });
  },

  updateEntry: (entryId: string, data: {
    title?: string;
    content?: string;
    mood?: 'great' | 'good' | 'okay' | 'low' | 'rough';
    energyLevel?: number;
    visibility?: 'private' | 'partners' | 'hierarchy';
  }) => api.patch(`/journal/entries/${entryId}`, data),

  deleteEntry: (entryId: string) => api.delete(`/journal/entries/${entryId}`),

  getEntryByDate: (date: string) => api.get(`/journal/entries/date/${date}`),

  // Calendar
  getCalendar: (year: number, month: number) =>
    api.get(`/journal/calendar/${year}/${month}`),

  // Media attachments
  attachMedia: (entryId: string, mediaIds: number[]) =>
    api.post(`/journal/entries/${entryId}/media`, { mediaIds }),

  removeMedia: (entryId: string, mediaId: number) =>
    api.delete(`/journal/entries/${entryId}/media/${mediaId}`),

  // Task links
  linkTask: (entryId: string, taskId: string, linkType?: 'reference' | 'reflection' | 'completion') =>
    api.post(`/journal/entries/${entryId}/tasks`, { taskId, linkType }),

  unlinkTask: (entryId: string, linkId: number) =>
    api.delete(`/journal/entries/${entryId}/tasks/${linkId}`),

  // Entry tags
  addTagToEntry: (entryId: string, tagId: number) =>
    api.post(`/journal/entries/${entryId}/tags`, { tagId }),

  removeTagFromEntry: (entryId: string, tagId: number) =>
    api.delete(`/journal/entries/${entryId}/tags/${tagId}`),

  // Tags CRUD
  getTags: () => api.get('/journal/tags'),

  createTag: (name: string, color?: string) =>
    api.post('/journal/tags', { name, color }),

  updateTag: (tagId: number, data: { name?: string; color?: string }) =>
    api.patch(`/journal/tags/${tagId}`, data),

  deleteTag: (tagId: number) => api.delete(`/journal/tags/${tagId}`),

  // Mood Check-ins
  quickMoodCheckin: (data: {
    mood: 'great' | 'good' | 'okay' | 'low' | 'rough';
    energyLevel?: number;
    note?: string;
    sharedWithDominant?: boolean;
    visibility?: 'private' | 'partners' | 'hierarchy';
  }) => api.post('/journal/mood', data),

  getMoodTrends: (days?: number, filter?: 'personal' | 'shared' | 'all') =>
    api.get('/journal/mood/trends', { params: { days, filter } }),

  getMoodStats: (days?: number, filter?: 'personal' | 'shared' | 'all') =>
    api.get('/journal/mood/stats', { params: { days, filter } }),

  getTodaysMood: () => api.get('/journal/mood/today'),

  getSharedMoods: (days?: number) =>
    api.get('/journal/mood/shared', { params: { days } }),

  getRecentMoods: (limit?: number) =>
    api.get('/journal/mood/recent', { params: { limit } }),

  // Scene Log
  createScene: (data: {
    notes: string;
    sceneDate?: string;
    durationMinutes?: number;
    partnerId?: number;
    intensityLevel?: number;
    satisfactionRating?: number;
    aftercareNotes?: string;
    safetyConcerns?: string;
    location?: string;
    sceneType?: 'play' | 'training' | 'punishment' | 'service' | 'protocol' | 'impact' | 'bondage' | 'sensation' | 'roleplay' | 'worship' | 'degradation' | 'pet_play' | 'wax' | 'edge_play' | 'discipline' | 'other';
    wasNegotiated?: boolean;
    mood?: 'great' | 'good' | 'okay' | 'low' | 'rough';
    energyLevel?: number;
    activities?: { activityId: number; personalRating?: number; notes?: string }[];
    mediaIds?: number[];
  }) => api.post('/journal/scenes', data),

  getScene: (sceneId: string) => api.get(`/journal/scenes/${sceneId}`),

  listScenes: (params?: {
    fromDate?: string;
    toDate?: string;
    sceneType?: 'play' | 'training' | 'punishment' | 'service' | 'protocol' | 'impact' | 'bondage' | 'sensation' | 'roleplay' | 'worship' | 'degradation' | 'pet_play' | 'wax' | 'edge_play' | 'discipline' | 'other';
    partnerId?: number;
    activityId?: number;
    limit?: number;
    offset?: number;
  }) => api.get('/journal/scenes', { params }),

  updateScene: (sceneId: string, data: {
    notes?: string;
    sceneDate?: string;
    durationMinutes?: number;
    partnerId?: number;
    intensityLevel?: number;
    satisfactionRating?: number;
    aftercareNotes?: string;
    safetyConcerns?: string;
    location?: string;
    sceneType?: 'play' | 'training' | 'punishment' | 'service' | 'protocol' | 'impact' | 'bondage' | 'sensation' | 'roleplay' | 'worship' | 'degradation' | 'pet_play' | 'wax' | 'edge_play' | 'discipline' | 'other';
    wasNegotiated?: boolean;
    mood?: 'great' | 'good' | 'okay' | 'low' | 'rough';
    energyLevel?: number;
    activities?: { activityId: number; personalRating?: number; notes?: string }[];
  }) => api.patch(`/journal/scenes/${sceneId}`, data),

  deleteScene: (sceneId: string) => api.delete(`/journal/scenes/${sceneId}`),

  // Scene Media
  uploadSceneMedia: (sceneId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/journal/scenes/${sceneId}/media/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  removeSceneMedia: (sceneId: string, mediaId: number) =>
    api.delete(`/journal/scenes/${sceneId}/media/${mediaId}`),

  getSceneStats: (days?: number) =>
    api.get('/journal/scenes/stats', { params: { days } }),

  getRecentScenes: (limit?: number) =>
    api.get('/journal/scenes/recent', { params: { limit } }),

  // Entry Sharing
  shareEntry: (entryId: string, userIds: number[], permission: 'view' | 'edit' = 'view') =>
    api.post(`/journal/entries/${entryId}/shares`, { userIds, permission }),
  getEntryShares: (entryId: string) =>
    api.get(`/journal/entries/${entryId}/shares`),
  removeEntryShare: (entryId: string, userId: number) =>
    api.delete(`/journal/entries/${entryId}/shares/${userId}`),
  getEntriesSharedWithMe: (organizationId?: string, params?: { limit?: number; offset?: number; search?: string }) =>
    api.get('/journal/shared-with-me', { params: { organization_id: organizationId, ...params } }),

  // Scene Sharing
  shareScene: (sceneId: string, userIds: number[], permission: 'view' | 'edit' = 'view') =>
    api.post(`/journal/scenes/${sceneId}/shares`, { userIds, permission }),
  getSceneShares: (sceneId: string) =>
    api.get(`/journal/scenes/${sceneId}/shares`),
  getBatchSceneShares: (sceneIds: string[]) =>
    api.post('/journal/scenes/shares/batch', { sceneIds }),
  removeSceneShare: (sceneId: string, userId: number) =>
    api.delete(`/journal/scenes/${sceneId}/shares/${userId}`),
  getScenesSharedWithMe: (organizationId?: string, params?: { limit?: number; offset?: number }) =>
    api.get('/journal/scenes/shared-with-me', { params: { organization_id: organizationId, ...params } }),
};

// Journal Prompts API (Spec 104) - Dom-assigned reflection prompts
export const journalPromptAPI = {
  // Create a prompt (admin only)
  create: (orgId: string, data: {
    promptText: string;
    assignedTo: string | number;
    guidanceNotes?: string;
    dueDate?: string;
  }) => api.post(`/organizations/${orgId}/journal-prompts`, data),

  // List prompts (role-filtered)
  list: (orgId: string, params?: { status?: 'pending' | 'completed' | 'expired' | 'dismissed' | 'all' }) =>
    api.get(`/organizations/${orgId}/journal-prompts`, { params }),

  // Get prompt templates
  getTemplates: (orgId: string) =>
    api.get(`/organizations/${orgId}/journal-prompts/templates`),

  // Get pending prompt count
  getPendingCount: (orgId: string) =>
    api.get(`/organizations/${orgId}/journal-prompts/pending-count`),

  // Respond to a prompt (sub links journal entry)
  respond: (promptId: string, journalEntryId: string) =>
    api.put(`/journal-prompts/${promptId}/respond`, { journalEntryId }),

  // Delete a pending prompt (admin only)
  delete: (promptId: string) =>
    api.delete(`/journal-prompts/${promptId}`),
};

// Relationship Review API - Scheduled relationship check-ins with threaded discussion
export const relationshipReviewAPI = {
  // Review Templates (schedules)
  getAll: (status?: string) =>
    api.get('/relationship-reviews', { params: status ? { status } : undefined }),
  getById: (id: string) => api.get(`/relationship-reviews/${id}`),
  create: (data: {
    dominantId: string | number;
    submissiveId: string | number;
    title: string;
    description?: string;
    category?: 'general' | 'emotional' | 'safety' | 'physical' | 'goals' | 'boundaries';
    scheduleType?: 'one_time' | 'weekly' | 'monthly' | 'quarterly' | 'semi_annual';
    scheduleFrequency?: number;
    scheduleDaysOfWeek?: number[];
    scheduleDayOfMonth?: number;
    nextReviewDate?: string;
  }) => api.post('/relationship-reviews', data),
  update: (id: string, data: Partial<{
    title: string;
    description: string;
    category: string;
    scheduleType: string;
    scheduleFrequency: number;
    scheduleDaysOfWeek: number[];
    scheduleDayOfMonth: number;
    nextReviewDate: string;
  }>) => api.put(`/relationship-reviews/${id}`, data),
  delete: (id: string) => api.delete(`/relationship-reviews/${id}`),
  pause: (id: string) => api.post(`/relationship-reviews/${id}/pause`),
  resume: (id: string) => api.post(`/relationship-reviews/${id}/resume`),
  // Spec 303 Fix 1
  accept: (id: string) => api.post(`/relationship-reviews/${id}/accept`),
  decline: (id: string) => api.post(`/relationship-reviews/${id}/decline`),

  // Review Instances (sessions)
  getInstances: (reviewId: string) =>
    api.get(`/relationship-reviews/${reviewId}/instances`),
  getOpenInstances: () => api.get('/relationship-reviews/instances/open'),
  getCompletedInstances: (limit?: number) =>
    api.get('/relationship-reviews/instances/completed', { params: limit ? { limit } : undefined }),
  getInstance: (instanceId: string) =>
    api.get(`/relationship-reviews/instances/${instanceId}`),
  signOff: (instanceId: string, data?: { summary?: string }) =>
    api.post(`/relationship-reviews/instances/${instanceId}/sign-off`, data),
  skipInstance: (instanceId: string, data?: { reason?: string }) =>
    api.post(`/relationship-reviews/instances/${instanceId}/skip`, data),
  rescheduleInstance: (instanceId: string, data: { scheduledDate: string }) =>
    api.post(`/relationship-reviews/instances/${instanceId}/reschedule`, data),
  createManualInstance: (reviewId: string, data?: { scheduledDate?: string }) =>
    api.post(`/relationship-reviews/${reviewId}/instances`, data),

  // Review Messages (discussion thread)
  getMessages: (instanceId: string) =>
    api.get(`/relationship-reviews/instances/${instanceId}/messages`),
  postMessage: (instanceId: string, data: {
    messageType?: 'update' | 'question' | 'response' | 'note';
    content: string;
    parentMessageId?: number;
  }) => api.post(`/relationship-reviews/instances/${instanceId}/messages`, data),
  updateMessage: (messageId: string, data: { content: string }) =>
    api.put(`/relationship-reviews/messages/${messageId}`, data),
  deleteMessage: (messageId: string) =>
    api.delete(`/relationship-reviews/messages/${messageId}`),
};
